import type { EntityState } from '@reduxjs/toolkit';
import {
	createAsyncThunk,
	createEntityAdapter,
	createSlice,
} from '@reduxjs/toolkit';
import type { IRestrictionList } from '~/Slices/schema';
import { unwrapFetch2 } from '~/fetch';
import type { IAppState } from '~/appstate';
import * as Hub from '../ogm-hub';

export const getRestrictionList = createAsyncThunk<
	IRestrictionList,
	number,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'restrictionLists/getRestrictionList',
	async (listId: number, { rejectWithValue }) => {
		const res = await unwrapFetch2(
			Hub.RestrictionsController.GetRestrictionList(listId)
		);
		if (res.errors.length === 0) {
			return { id: listId, name: res.restrictionListName };
		}
		return rejectWithValue(null);
	}
);

export interface IEditRestrictionListParameters {
	listId: number;
	newName: string;
}

export const editRestrictionList = createAsyncThunk<
	IRestrictionList,
	IEditRestrictionListParameters,
	{ state: IAppState; rejectValue: string[] | Response }
>(
	'restrictionLists/editRestrictionList',
	async (params: IEditRestrictionListParameters, { rejectWithValue }) => {
		const res = await unwrapFetch2(
			Hub.RestrictionsController.EditRestrictionList({
				listId: params.listId,
				newName: params.newName,
			})
		);
		if (res.errors.length === 0) {
			return { id: params.listId, name: params.newName };
		} else {
			return rejectWithValue(res.errors);
		}
	}
);

export type IRestrictionListsState = EntityState<IRestrictionList>;

const restrictionListsAdapter = createEntityAdapter<IRestrictionList>();

export const restrictionListsSlice = createSlice({
	name: 'restrictionLists',
	initialState: restrictionListsAdapter.getInitialState(),
	reducers: {},
	extraReducers: (builder) => {
		builder.addCase(getRestrictionList.fulfilled, (state, action) => {
			restrictionListsAdapter.upsertOne(state, action.payload);
		});
		builder.addCase(editRestrictionList.fulfilled, (state, action) => {
			restrictionListsAdapter.upsertOne(state, action.payload);
		});
	},
});

export function selectRestrictionList(state, id: number): IRestrictionList {
	return state.restrictionLists.entities[id];
}
